import React, { useState, useEffect } from 'react';
import { CheckCircle2, Package, Trash2, Plus, Edit2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getPrendasRecoleccion, updatePrenda, deletePrenda, createPrenda } from '../services/deposito.services';
import { getAll as getAllGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';

const ModalVerificarRecoleccion = ({ isOpen, onClose, recoleccion, onVerificar }) => {
  const { toast } = useToast();
  const [prendas, setPrendas] = useState([]);
  const [editandoPrenda, setEditandoPrenda] = useState(null);
  const [agregandoPrenda, setAgregandoPrenda] = useState(false);
  const [tiposPrendas, setTiposPrendas] = useState([]);
  const [nuevaPrenda, setNuevaPrenda] = useState({
    idPrenda: '',
    nombre: '',
    descripcion: '',
    cantidad: 1
  });
  const [loading, setLoading] = useState(false);
  const [loadingPrendas, setLoadingPrendas] = useState(false);
  const [deletingPrendaId, setDeletingPrendaId] = useState(null);

  useEffect(() => {
    const loadPrendas = async () => {
      if (recoleccion && isOpen) {
        setLoadingPrendas(true);
        try {
          const recoleccionId = recoleccion._id || recoleccion.id;     
          const response = await getPrendasRecoleccion(recoleccionId);
          setPrendas(response?.data || []);
        } catch (error) {
          console.error('Error cargando prendas:', error);
          toast({
            title: 'Error',
            description: 'No se pudieron cargar las prendas de la recolección',
            variant: 'destructive'
          });
          setPrendas([]);
        } finally {
          setLoadingPrendas(false);
        }
      } 
    };

    const loadTiposPrendas = async () => {
      if (isOpen) {
        try {
          const response = await getAllGarmentTypes();
          setTiposPrendas(response?.data || []);
        } catch (error) {
          console.error('Error cargando tipos de prendas:', error);
          toast({
            title: 'Error',
            description: 'No se pudieron cargar los tipos de prendas',
            variant: 'destructive'
          });
        }
      }
    };

    loadPrendas();
    loadTiposPrendas();
  }, [recoleccion, isOpen, toast]);

  const handleEditarPrenda = (prenda) => {
    setEditandoPrenda({ ...prenda });
  };

  const handleGuardarEdicion = async () => {
    if (!editandoPrenda.nombre) {
      toast({
        title: 'Error',
        description: 'El nombre es requerido',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Actualizar en el backend
      const recoleccionId = recoleccion._id || recoleccion.id;
      const prendaId = editandoPrenda._id || editandoPrenda.id;
      
      await updatePrenda(recoleccionId, prendaId, {
        nombre: editandoPrenda.nombre,
        descripcion: editandoPrenda.descripcion,
        observaciones: editandoPrenda.observaciones,
        cantidad: parseInt(editandoPrenda.cantidad) || 1,
        estado:'recibido'
      });

      // Actualizar en el estado local
      setPrendas(prev => prev.map(p => 
        (p._id || p.id) === prendaId ? editandoPrenda : p
      ));
      
      setEditandoPrenda(null);
      toast({
        title: 'Prenda actualizada',
        description: 'Los cambios se guardaron en la base de datos'
      });
    } catch (error) {
      console.error('Error actualizando prenda:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la prenda',
        variant: 'destructive'
      });
    }
  };

  const handleEliminarPrenda = async (prenda) => {
    if (!window.confirm('¿Está seguro de eliminar esta prenda?')) {
      return;
    }

    const prendaId = prenda._id || prenda.id;
    setDeletingPrendaId(prendaId);
    try {
      const recoleccionId = recoleccion._id || recoleccion.id;
      await deletePrenda(recoleccionId, prendaId);
      setPrendas(prev => prev.filter(p => (p._id || p.id) !== prendaId));
      toast({ title: 'Prenda eliminada', description: 'La prenda se eliminó de la base de datos' });
    } catch (error) {
      console.error('Error eliminando prenda:', error);
      toast({ title: 'Error', description: 'No se pudo eliminar la prenda', variant: 'destructive' });
    } finally {
      setDeletingPrendaId(null);
    }
  };

  const handleAgregarPrenda = async () => {
    if (!nuevaPrenda.idPrenda) {
      toast({
        title: 'Error',
        description: 'Debe seleccionar un tipo de prenda',
        variant: 'destructive'
      });
      return;
    }

    try {
      const recoleccionId = recoleccion._id || recoleccion.id;
      
      const prendaData = {
        idPrenda: nuevaPrenda.idPrenda,
        nombre: nuevaPrenda.nombre,
        descripcion: nuevaPrenda.descripcion,
        cantidad: parseInt(nuevaPrenda.cantidad) || 1,
        estado: 'recibido'
      };

      const response = await createPrenda(recoleccionId, prendaData);
      
      // Agregar al estado local
      const prendaAgregada = {
        ...prendaData,
        id: response.data?.id || response.data?._id || Date.now(),
        _id: response.data?.id || response.data?._id || Date.now()
      };
      
      setPrendas(prev => [...prev, prendaAgregada]);
      
      // Resetear formulario
      setNuevaPrenda({
        idPrenda: '',
        nombre: '',
        descripcion: '',
        cantidad: 1
      });
      setAgregandoPrenda(false);
      
      toast({
        title: 'Prenda agregada',
        description: 'La prenda se agregó correctamente'
      });
    } catch (error) {
      console.error('Error agregando prenda:', error);
      toast({
        title: 'Error',
        description: 'No se pudo agregar la prenda',
        variant: 'destructive'
      });
    }
  };

  const handleSeleccionarTipoPrenda = (tipo) => {
    setNuevaPrenda({
      ...nuevaPrenda,
      idPrenda: tipo._id,
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || ''
    });
  };

  const handleVerificar = async () => {
    if (prendas.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe haber al menos una prenda para verificar',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Preparar payload limpio sin datos anidados que causan problemas de validación
      const recoleccionActualizada = {
        _id: recoleccion._id || recoleccion.id,
        consecutivo: recoleccion.consecutivo,
        estado: 'recibido',
        cantidad_prendas: prendas.reduce((sum, p) => sum + (parseInt(p.cantidad) || 0), 0),
        // Solo IDs, no objetos completos
        planta: recoleccion.planta?.id || recoleccion.planta,
        empleado: recoleccion.empleado?.id || recoleccion.empleado,
        casillero: recoleccion.casillero || 'N/A'
      };

      await onVerificar(recoleccionActualizada);

      toast({
        title: 'Recolección verificada',
        description: 'La recolección ha sido verificada exitosamente'
      });

      onClose();
    } catch (error) {
      console.error('Error verificando recolección:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo verificar la recolección',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPrendas = prendas.reduce((sum, p) => sum + (parseInt(p.cantidad) || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Verificar Recolección de Prendas
          </DialogTitle>
        </DialogHeader>

        {recoleccion && (
          <div className="space-y-6">
            {/* Información del empleado */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-semibold text-blue-700 dark:text-blue-300">
                    {recoleccion.empleado?.nombre + ' ' + recoleccion.empleado?.apellido}
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">Cédula: {recoleccion.empleado?.cedula}</span>
                  <span className="text-muted-foreground">•</span>
                </div>
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                  Pendiente Verificación
                </Badge>
              </div>
            </div>

            {/* Lista de prendas */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-500" />
                  Prendas Registradas ({prendas.length})
                </h3>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    onClick={() => setAgregandoPrenda(!agregandoPrenda)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Prenda
                  </Button>
                  <div className="text-sm text-muted-foreground">
                    Total: <span className="font-semibold text-foreground">{totalPrendas}</span> prendas
                  </div>
                </div>
              </div>

              {/* Formulario para agregar nueva prenda */}
              {agregandoPrenda && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-medium text-sm mb-3 text-blue-700 dark:text-blue-300">
                    Nueva Prenda
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Tipo de Prenda *</Label>
                        <select
                          value={nuevaPrenda.idPrenda}
                          onChange={(e) => {
                            const tipo = tiposPrendas.find(t => t._id === e.target.value);
                            if (tipo) {
                              handleSeleccionarTipoPrenda(tipo);
                            }
                          }}
                          className="w-full h-8 px-2 text-xs border rounded bg-white dark:bg-gray-800"
                        >
                          <option value="">Seleccionar...</option>
                          {tiposPrendas.map((tipo) => (
                            <option key={tipo._id} value={tipo._id}>
                              {tipo.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          value={nuevaPrenda.cantidad}
                          onChange={(e) => setNuevaPrenda({ ...nuevaPrenda, cantidad: e.target.value })}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAgregarPrenda}>
                        <Plus className="h-4 w-4 mr-1" />
                        Agregar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        setAgregandoPrenda(false);
                        setNuevaPrenda({
                          idPrenda: '',
                          nombre: '',
                          descripcion: '',
                          cantidad: 1
                        });
                      }}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {loadingPrendas ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Cargando prendas...</span>
                  </div>
                ) : prendas.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay prendas registradas
                  </div>
                ) : (
                  prendas.map((prenda, index) => (
                    <div
                      key={prenda.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border"
                    >
                      {editandoPrenda?.id === prenda.id ? (
                        // Modo edición
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">Tipo de Prenda</Label>
                              <Input
                                value={editandoPrenda.nombre}
                                disabled
                                className="h-8 bg-gray-100"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Descripción *</Label>
                              <Input
                                value={editandoPrenda.descripcion}
                                onChange={(e) => setEditandoPrenda({ ...editandoPrenda, descripcion: e.target.value })}
                                className="h-8"
                                placeholder="Color, talla..."
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Cantidad</Label>
                              <Input
                                type="number"
                                min="1"
                                value={editandoPrenda.cantidad}
                                onChange={(e) => setEditandoPrenda({ ...editandoPrenda, cantidad: e.target.value })}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Observaciones</Label>
                            <Input
                              value={editandoPrenda.observaciones || ''}
                              onChange={(e) => setEditandoPrenda({ ...editandoPrenda, observaciones: e.target.value })}
                              className="h-8"
                              placeholder="Opcional"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleGuardarEdicion}>
                              Guardar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditandoPrenda(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Modo vista
                        <>
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-medium text-sm bg-primary/10 text-primary px-2 py-1 rounded">
                                #{index + 1}
                              </span>
                              <div>
                                <p className="font-medium">{prenda.nombre}</p>
                                <p className="text-sm text-muted-foreground">{prenda.descripcion}</p>
                                {prenda.observaciones && (
                                  <p className="text-xs text-muted-foreground italic">
                                    Obs: {prenda.observaciones}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium bg-blue-100 text-blue-700 px-3 py-1 rounded">
                              Cant: {prenda.cantidad}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditarPrenda(prenda)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEliminarPrenda(prenda)}
                              disabled={deletingPrendaId === (prenda._id || prenda.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              {deletingPrendaId === (prenda._id || prenda.id)
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Trash2 className="h-4 w-4" />
                              }
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Resumen */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-300">
                    ¿Confirmar verificación?
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Se registrarán {prendas.length} tipo(s) de prenda con un total de {totalPrendas} unidades
                  </p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleVerificar} 
            disabled={loading || prendas.length === 0}
            className="bg-green-600 hover:bg-green-700 min-w-[150px]"
          >
            {loading ? 'Verificando...' : 'Verificar Recolección'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalVerificarRecoleccion;
